import SearchIcon from '@mui/icons-material/Search';
import { TextField, Theme } from '@mui/material';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import BoxDrawer from 'components/BoxDrawer';
import IconButtonCancel from 'components/Buttons/IconButtonCancel';
import { SpinnerIndicator } from 'components/CallServer';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiLink from 'components/MuiExtras/MuiLink';
import MuiPagination from 'components/MuiExtras/MuiPagination';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import UsernameInspect from 'components/UsernameInspect';
import { DashboardKnowledgeIndicatorsDetailColumn, DashboardKnowledgeIndicatorsDrillDownModel, DashboardKnowledgeIndicatorsGraphGroup, DashboardKnowledgeIndicatorsSummaryColumn, DashboardUserAndGroupsAndManagersBaseFilter, PagedResult } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation } from 'react-i18next';
import { getTopicUrl, getUserProfileUrl } from 'views/Timeline/Components/FeedHelpers';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({
    table: {
        margin: 0,
        padding: 0,
        width: "100%"
    },
    tableCellStretch: {
        verticalAlign: "middle",
        paddingBottom: theme.spacing,
        width: 1
    },
    tableCell: {
        verticalAlign: "middle",
        paddingBottom: theme.spacing,
    }
});

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    ds: PagedResult<DashboardKnowledgeIndicatorsDrillDownModel>;
    offset: number;
    loading: boolean;
    searchText: string;
}

interface IProps {
    defaultGraphGroup: DashboardKnowledgeIndicatorsGraphGroup;
    summaryColumn: DashboardKnowledgeIndicatorsSummaryColumn;
    detailColumn: DashboardKnowledgeIndicatorsDetailColumn;
    selectedValue?: number;
    filter: DashboardUserAndGroupsAndManagersBaseFilter;
    drillDownId: string;
    onClose: () => void;
}

type Props = IProps & WithStyles<typeof styles> & WithTranslation;

const rowsPerPage = 20;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class KnowledgeTestIndicatorsDrawer extends React.PureComponent<Props, IState> {

    private searchRef: any = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            ds: null,
            offset: 0,
            loading: true,
            searchText: ""
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.KnowledgeTest.IndicatorsDrawer." + r);
    }

    ///
    private get showRightColumn() {
        if (this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge)
            return (this.props.detailColumn == DashboardKnowledgeIndicatorsDetailColumn.UserBetter
                || this.props.detailColumn == DashboardKnowledgeIndicatorsDetailColumn.UserWorst
                || this.props.detailColumn == DashboardKnowledgeIndicatorsDetailColumn.UserLevel
                || this.props.detailColumn == DashboardKnowledgeIndicatorsDetailColumn.TopicBetter
                || this.props.detailColumn == DashboardKnowledgeIndicatorsDetailColumn.TopicWorst
                || this.props.detailColumn == DashboardKnowledgeIndicatorsDetailColumn.TopicLevel
            );

        return true;
    }

    ///
    private getTitle() {
        switch (this.props.detailColumn) {
            case DashboardKnowledgeIndicatorsDetailColumn.UserBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.UserWorst:
            case DashboardKnowledgeIndicatorsDetailColumn.UserLevel:
            case DashboardKnowledgeIndicatorsDetailColumn.UserQt:
            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel:
                return this.res("TopicTitle");

            case DashboardKnowledgeIndicatorsDetailColumn.TopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicWorst:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicLevel:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicQt:
                return this.res("UserTitle");
        }
        return null;
    }

    ///
    private getRightText() {
        return this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge
            ? this.res("KnowledgeText")
            : this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Result
                ? this.res("ResultText")
                : this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Completed
                    ? this.res("CompletedText")
                    : this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Trust
                        ? this.res("TrustText")
                        : this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Revision
                            ? this.res("RevisionText")
                            : null;
    }

    ///
    private getUrl(id: string) {
        switch (this.props.detailColumn) {
            case DashboardKnowledgeIndicatorsDetailColumn.UserBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.UserWorst:
            case DashboardKnowledgeIndicatorsDetailColumn.UserLevel:
            case DashboardKnowledgeIndicatorsDetailColumn.UserQt:
            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.UserTopicLevel:
                return getTopicUrl(id);

            case DashboardKnowledgeIndicatorsDetailColumn.TopicBetter:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicWorst:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicLevel:
            case DashboardKnowledgeIndicatorsDetailColumn.TopicQt:
                return getUserProfileUrl(id);
        }
        return null;
    }

    ///
    private getData(offset: number) {

        this.setState({ loading: true });

        api.getDashboardKnowledgeTestIndicators(
            {
                ...this.props.filter,
                defaultGraphGroup: this.props.defaultGraphGroup,
                columnToSummary: this.props.summaryColumn,
                detailColumn: this.props.detailColumn,
                selectedValue: this.props.selectedValue,
                drillDownId: this.props.drillDownId,
                drillDownSearchText: this.state.searchText,
                range: { offset: offset, take: 10 }
            })
            .then((result) => {
                this.setState({ ds: result as PagedResult<DashboardKnowledgeIndicatorsDrillDownModel>, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ ds: null, loading: false, offset: 0 });
            });
    }

    ///
    private handleSearch(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.keyCode == 13) this.getData(0);
    }

    ///
    private renderContent() {
        const { ds, loading } = this.state;
        const { classes } = this.props;

        const rightText = this.showRightColumn ? this.getRightText() : null;

        return (
            <GridEx container fullHeight direction="column" wrap="nowrap">
                <GridEx>

                    <MuiContainer>
                        <TextField
                            fullWidth
                            placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                            value={this.state.searchText}
                            onChange={() => this.setState({ searchText: this.searchRef.value })}
                            onKeyDown={(e) => this.handleSearch(e)}
                            onBlur={() => this.getData(0)}
                            inputRef={(ref) => this.searchRef = ref}
                            InputProps={
                                {
                                    startAdornment: <SearchIcon />,
                                    endAdornment: this.state.searchText != ""
                                        ? <IconButtonCancel
                                            size="small"
                                            onClick={
                                                () => {
                                                    this.setState({ searchText: "" }, () => this.getData(0));
                                                }
                                            } />
                                        : null
                                }
                            }
                        />
                    </MuiContainer>

                </GridEx>
                <GridEx item xs overflow="auto">
                    {
                        loading
                            ? <SpinnerIndicator />
                            : (
                                <table className={classes.table}>
                                    {
                                        rightText &&
                                        <thead>
                                            <tr>
                                                <td></td>
                                                <td></td>
                                                <td align="right" className={classes.tableCell}><b>{rightText}</b></td>
                                            </tr>
                                        </thead>
                                    }
                                    <tbody>
                                        {
                                            ds.items.map((v, index) => {

                                                const url = this.getUrl(v.id);

                                                const name = this.showRightColumn
                                                    ? v.name
                                                    : <UsernameInspect user={v} />;

                                                const right = this.showRightColumn
                                                    ? (
                                                        this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Knowledge
                                                            ? <span>{v.knowledgePercent.toFixed(1)}</span>
                                                            : this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Result
                                                                ? <span>{v.resultPercent.toFixed(1)}</span>
                                                                : this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Completed
                                                                    ? <span>{v.completedPercent.toFixed(1)}</span>
                                                                    : this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Trust
                                                                        ? <span>{v.trustPercent.toFixed(1)}</span>
                                                                        : this.props.summaryColumn == DashboardKnowledgeIndicatorsSummaryColumn.Revision
                                                                            ? <span>{v.revisionPercent.toFixed(1)}</span>
                                                                            : null
                                                    ) : null;




                                                return (
                                                    <tr key={index}>
                                                        <td className={classes.tableCellStretch}>
                                                            <img src={v.thumbnailUrl} alt={v.name} width="32" height="32" />
                                                        </td>
                                                        <td className={classes.tableCell}>
                                                            <MuiLink color="secondary" href={url} target="_blank" >
                                                                {name}
                                                            </MuiLink>
                                                        </td>
                                                        {
                                                            right != null &&
                                                            <td align="right" className={classes.tableCell}>
                                                                <span>{right}</span>
                                                            </td>
                                                        }
                                                    </tr>
                                                );
                                            })
                                        }
                                    </tbody>
                                </table>
                            )
                    }
                </GridEx>

                {
                    !loading &&
                    <GridEx item>
                        <MuiPagination
                            limit={rowsPerPage}
                            offset={this.state.offset}
                            total={ds.total}
                            onClick={(e, offset) => this.getData(offset)}
                        />
                    </GridEx>
                }
            </GridEx>
        );
    }

    ///
    public componentDidMount() {
        this.getData(0);
    }

    ///
    public render() {
        const { onClose } = this.props;

        return (
            <BoxDrawer
                open
                overflow="hidden"
                onClose={() => onClose()}
                title={this.getTitle()}
            >
                <MuiPaper fullHeight elevation={0}>
                    {this.renderContent()}
                </MuiPaper>
            </BoxDrawer>
        );
    }
}

export default withStyles(styles)(KnowledgeTestIndicatorsDrawer);